import React, { forwardRef } from 'react';
import { ShiftReportItem, ReceiptItem } from '../../../model/report';
import { printDocument } from '../../../utils/print-utils';
import '../../../assets/css/thermal-slip.css';

interface ShiftSlipProps {
    shift: ShiftReportItem;
    onClose?: () => void;
}

interface ReceiptSlipProps {
    receipt: ReceiptItem;
    onClose?: () => void;
}

export const printShiftThermalSlip = (element: HTMLElement | null, shiftNo?: string) => {
    printDocument(element, {
        documentType: 'thermal-80mm',
        title: `ใบสรุปปิดกะ ${shiftNo || ''}`
    });
};

export const printReceiptThermalSlip = (element: HTMLElement | null, billNo?: string) => {
    printDocument(element, {
        documentType: 'thermal-80mm',
        title: `ใบเสร็จรับเงิน ${billNo || ''}`
    });
};

// 1. 80mm Thermal Slip for Work Shift Z-Report
export const ShiftThermalSlip80mm = forwardRef<HTMLDivElement, ShiftSlipProps>(({ shift }, ref) => {
    const entry = shift.entry_breakdown || { b1000: 0, b500: 0, b100: 0, b50: 0, b20: 0, c10: 0, c5: 0, c2: 0, c1: 0, c050: 0, c025: 0 };
    const close = shift.close_breakdown || { b1000: 0, b500: 0, b100: 0, b50: 0, b20: 0, c10: 0, c5: 0, c2: 0, c1: 0, c050: 0, c025: 0 };

    return (
        <div className="thermal-slip-container-80mm" id="printable-shift-slip" ref={ref} data-testid="shift-thermal-slip-80mm">
            {/* Header */}
            <div className="slip-header">
                <div className="slip-store-name">JinDa POS</div>
                <div className="slip-sub">ใบสรุปปิดกะการทำงาน (Z-REPORT)</div>
                <div className="slip-divider-dashed"></div>
            </div>

            {/* Shift Meta */}
            <div className="slip-meta">
                <div className="slip-row"><span>รหัสกะ:</span><strong>{shift.shift_no}</strong></div>
                <div className="slip-row"><span>เครื่อง (Device):</span><span>{shift.device || 'POS-01'}</span></div>
                <div className="slip-row"><span>แคชเชียร์:</span><span>{shift.cashier_name}</span></div>
                <div className="slip-row"><span>เวลาเปิดกะ:</span><span>{shift.open_date}</span></div>
                <div className="slip-row"><span>เวลาปิดกะ:</span><span>{shift.close_date || 'ยังไม่ปิดกะ'}</span></div>
                {shift.closed_by_name && (
                    <div className="slip-row"><span>ผู้ปิดกะ:</span><span>{shift.closed_by_name}</span></div>
                )}
            </div>

            <div className="slip-divider-dashed"></div>

            {/* Sales Summary */}
            <div className="slip-section-title">สรุปยอดขายประจำกะ</div>
            <div className="slip-meta">
                <div className="slip-row">
                    <span>เงินทอนเปิดกะ:</span>
                    <span>฿{shift.open_amount.toLocaleString()}</span>
                </div>
                <div className="slip-row">
                    <span>ยอดขายเงินสด:</span>
                    <span>฿{shift.cash_sales.toLocaleString()}</span>
                </div>
                <div className="slip-row">
                    <span>ยอดขายโอน/QR:</span>
                    <span>฿{shift.transfer_sales.toLocaleString()}</span>
                </div>
                {shift.credit_sales ? (
                    <div className="slip-row">
                        <span>ยอดขายบัตรเครดิต:</span>
                        <span>฿{shift.credit_sales.toLocaleString()}</span>
                    </div>
                ) : null}
                <div className="slip-divider-solid"></div>
                <div className="slip-row slip-highlight">
                    <span>ยอดขายรวมสุทธิ:</span>
                    <span>฿{shift.total_sales.toLocaleString()}</span>
                </div>
                <div className="slip-row">
                    <span>จำนวนบิลขาย:</span>
                    <span>{shift.bill_count || '-'} บิล</span>
                </div>
            </div>

            <div className="slip-divider-dashed"></div>

            {/* Cash Drawer Counting Breakdown */}
            <div className="slip-section-title">รายละเอียดการนับเงินสดปิดกะ</div>
            <div className="slip-meta slip-table-compact">
                {close.b1000 > 0 && <div className="slip-row"><span>ธนบัตร 1,000 x {close.b1000}:</span><span>฿{(close.b1000 * 1000).toLocaleString()}</span></div>}
                {close.b500 > 0 && <div className="slip-row"><span>ธนบัตร 500 x {close.b500}:</span><span>฿{(close.b500 * 500).toLocaleString()}</span></div>}
                {close.b100 > 0 && <div className="slip-row"><span>ธนบัตร 100 x {close.b100}:</span><span>฿{(close.b100 * 100).toLocaleString()}</span></div>}
                {close.b50 > 0 && <div className="slip-row"><span>ธนบัตร 50 x {close.b50}:</span><span>฿{(close.b50 * 50).toLocaleString()}</span></div>}
                {close.b20 > 0 && <div className="slip-row"><span>ธนบัตร 20 x {close.b20}:</span><span>฿{(close.b20 * 20).toLocaleString()}</span></div>}
                {close.c10 > 0 && <div className="slip-row"><span>เหรียญ 10 x {close.c10}:</span><span>฿{(close.c10 * 10).toLocaleString()}</span></div>}
                {close.c5 > 0 && <div className="slip-row"><span>เหรียญ 5 x {close.c5}:</span><span>฿{(close.c5 * 5).toLocaleString()}</span></div>}
                {close.c2 > 0 && <div className="slip-row"><span>เหรียญ 2 x {close.c2}:</span><span>฿{(close.c2 * 2).toLocaleString()}</span></div>}
                {close.c1 > 0 && <div className="slip-row"><span>เหรียญ 1 x {close.c1}:</span><span>฿{(close.c1 * 1).toLocaleString()}</span></div>}
                {close.c050 > 0 && <div className="slip-row"><span>เหรียญ 0.50 x {close.c050}:</span><span>฿{(close.c050 * 0.5).toLocaleString()}</span></div>}
                {close.c025 > 0 && <div className="slip-row"><span>เหรียญ 0.25 x {close.c025}:</span><span>฿{(close.c025 * 0.25).toLocaleString()}</span></div>}
            </div>

            <div className="slip-divider-solid"></div>

            {/* Audit Variance */}
            <div className="slip-meta">
                <div className="slip-row">
                    <span>เงินสดที่ควรมี (Expected):</span>
                    <span>฿{shift.expected_cash.toLocaleString()}</span>
                </div>
                <div className="slip-row">
                    <span>เงินสดนับได้จริง (Counted):</span>
                    <strong>฿{shift.close_amount.toLocaleString()}</strong>
                </div>
                <div className="slip-row slip-diff-row">
                    <span>ผลต่างเงินสด (Diff):</span>
                    <strong>
                        {shift.status === 0 ? 'ยังไม่ปิดกะ' :
                         shift.cash_diff === 0 ? '฿0 (ตรงพอดี)' :
                         shift.cash_diff > 0 ? `+฿${shift.cash_diff.toLocaleString()} (เงินเกิน)` :
                         `-฿${Math.abs(shift.cash_diff).toLocaleString()} (เงินขาด)`}
                    </strong>
                </div>
            </div>

            <div className="slip-divider-dashed"></div>

            {/* Signatures */}
            <div className="slip-signature-box">
                <div className="slip-sign-line">
                    <div>ลงชื่อผู้ส่งกะ (แคชเชียร์)</div>
                    <div className="slip-sign-dots">................................................</div>
                </div>
                <div className="slip-sign-line">
                    <div>ลงชื่อผู้รับกะ / ผู้จัดการ</div>
                    <div className="slip-sign-dots">................................................</div>
                </div>
            </div>

            {/* Footer */}
            <div className="slip-footer">
                <div>พิมพ์เมื่อ: {new Date().toLocaleString('th-TH')}</div>
                <div>*** ขอบคุณที่ใช้บริการ JinDa POS ***</div>
            </div>
        </div>
    );
});

// 2. 80mm Thermal Slip for Sales Receipt
export const ReceiptThermalSlip80mm = forwardRef<HTMLDivElement, ReceiptSlipProps>(({ receipt }, ref) => {
    return (
        <div className="thermal-slip-container-80mm" id="printable-receipt-slip" ref={ref} data-testid="receipt-thermal-slip-80mm">
            <div className="slip-header">
                <div className="slip-store-name">JinDa POS Coffee & Bakery</div>
                <div className="slip-sub">ใบเสร็จรับเงินอย่างย่อ</div>
                <div className="slip-divider-dashed"></div>
            </div>

            <div className="slip-meta">
                {/* Blank only against a Backend that predates the sale
                    endpoint returning its document number. Saying so beats
                    an empty line, and beats the invented number that used
                    to fill it. */}
                <div className="slip-row"><span>เลขที่บิล:</span><strong>{receipt.bill_no || 'ไม่ทราบ'}</strong></div>
                <div className="slip-row"><span>วันที่:</span><span>{receipt.create_date}</span></div>
                <div className="slip-row"><span>แคชเชียร์:</span><span>{receipt.cashier_name}</span></div>
                {receipt.member_name && receipt.member_name !== '-' && (
                    <div className="slip-row"><span>สมาชิก:</span><span>{receipt.member_name}</span></div>
                )}
                <div className="slip-row"><span>การชำระ:</span><span>{receipt.payment_type_name}</span></div>
            </div>

            <div className="slip-divider-dashed"></div>

            {receipt.items && receipt.items.length > 0 && (
                <div className="slip-items-container">
                    <div className="slip-items-header">
                        <span>รายการ</span>
                        <span>จำนวน</span>
                        <span>รวม</span>
                    </div>
                    <div className="slip-divider-solid"></div>
                    {receipt.items.map((item, idx) => (
                        <div key={idx} className="slip-item-row">
                            <div className="slip-item-name">{item.product_name}</div>
                            <div className="slip-item-calc">
                                <span>{item.quantity} x ฿{item.price}</span>
                                <span>฿{item.total_price}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="slip-divider-dashed"></div>

            <div className="slip-meta">
                <div className="slip-row"><span>ยอดรวมสินค้า:</span><span>฿{receipt.total_price.toLocaleString()}</span></div>
                {(receipt.discount > 0 || receipt.discount_bill > 0) && (
                    <div className="slip-row">
                        <span>ส่วนลด:</span>
                        <span>-฿{(receipt.discount + receipt.discount_bill).toLocaleString()}</span>
                    </div>
                )}
                <div className="slip-row"><span>ภาษี (VAT 7%):</span><span>฿{receipt.vat_amount.toLocaleString()}</span></div>
                <div className="slip-divider-solid"></div>
                <div className="slip-row slip-highlight">
                    <span>ยอดชำระสุทธิ:</span>
                    <span>฿{receipt.net_price.toLocaleString()}</span>
                </div>
                <div className="slip-row"><span>รับเงิน:</span><span>฿{receipt.receive.toLocaleString()}</span></div>
                <div className="slip-row"><span>เงินทอน:</span><span>฿{receipt.change.toLocaleString()}</span></div>
            </div>

            <div className="slip-divider-dashed"></div>

            <div className="slip-footer">
                <div>พิมพ์เมื่อ: {new Date().toLocaleString('th-TH')}</div>
                <div>*** ขอบคุณที่ไว้วางใจใช้บริการ ***</div>
            </div>
        </div>
    );
});
